import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeftRightIcon, CheckIcon, ClockIcon, MapPinIcon, TriangleAlertIcon, UsersIcon, XIcon } from 'lucide-react';
import { swapOffers, type Registration, type SwapOffer } from '../../data/loisirs';
interface SwapDialogProps {
  registration: Registration | null;
  onClose: () => void;
  onSwapped: (registrationId: string, offer: SwapOffer) => void;
}
const fitStyles: Record<SwapOffer['fit'], string> = {
  ideal: 'bg-ville-mist text-ville-deep',
  possible: 'bg-slate-100 text-slate-600',
  conflit: 'bg-red-50 text-ville-coral'
};
export function SwapDialog({
  registration,
  onClose,
  onSwapped
}: SwapDialogProps) {
  const [selected, setSelected] = useState<string | null>(null);
  const [sent, setSent] = useState<SwapOffer | null>(null);
  useEffect(() => {
    if (!registration) {
      setSelected(null);
      setSent(null);
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previous;
    };
  }, [registration, onClose]);
  const offers = registration ? swapOffers.filter(offer => offer.registrationId === registration.id) : [];
  const confirm = () => {
    const offer = offers.find(item => item.id === selected);
    if (!offer || !registration) return;
    setSent(offer);
    onSwapped(registration.id, offer);
  };
  return <AnimatePresence data-id="element-2355" data-mp="12tjbow9bw3rc">
      {registration && <motion.div className="fixed inset-0 z-[60] flex items-end justify-center sm:items-center" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: [0.23, 1, 0.32, 1]
    }} data-id="element-2356" data-mp="12uug8yallfji">
          <button type="button" aria-label="Fermer" onClick={onClose} className="absolute inset-0 bg-ville-navy/45" data-id="element-2357" data-mp="18it2kgsocty9" />

          <motion.div role="dialog" aria-modal="true" aria-label={`Échanger le créneau de ${registration.activity}`} initial={{
        y: 24,
        opacity: 0,
        scale: 0.98
      }} animate={{
        y: 0,
        opacity: 1,
        scale: 1
      }} exit={{
        y: 16,
        opacity: 0
      }} transition={{
        duration: 0.25,
        ease: [0.23, 1, 0.32, 1]
      }} className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-t-3xl bg-white p-6 shadow-2xl sm:rounded-3xl sm:p-8" data-id="element-2358" data-mp="1hhxzjiol8f8b">
            <div className="flex items-start justify-between gap-4" data-id="element-2359" data-mp="256qogv6slmtf">
              <div className="min-w-0" data-id="element-2360" data-mp="2a7n2i1kuypck">
                <p className="text-xs font-bold uppercase tracking-wider text-ville-indigo" data-id="element-2361" data-mp="2a8ogoex67ryi">
                  Échange de créneau
                </p>
                <h2 className="mt-1 text-xl font-extrabold tracking-tight text-ville-navy" data-id="element-2362" data-mp="2p9vanq2w9ul3">
                  {registration.activity}
                </h2>
                <p className="mt-1 text-sm text-slate-600" data-id="element-2363" data-mp="2k9qpvnuyedp2">
                  {registration.participant} · actuellement {registration.schedule}
                </p>
              </div>
              <button type="button" onClick={onClose} aria-label="Fermer" className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-slate-500 transition-colors duration-150 ease-ville hover:bg-slate-100 hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-2364" data-mp="2bbosrjecc572">
                <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-2365" data-mp="2u86ftni8vgwz" />
              </button>
            </div>

            {sent ? <div className="mt-6" data-id="element-2366" data-mp="3qsfaaeakgs3i">
                <span className="grid h-12 w-12 place-items-center rounded-full bg-ville-mist text-ville-deep" aria-hidden="true" data-id="element-2367" data-mp="3m0npkke3dgla">
                  <CheckIcon className="h-6 w-6" data-id="element-2368" data-mp="3bhh6cgnz2hl4" />
                </span>
                <h3 className="mt-3 text-lg font-extrabold text-ville-navy" data-id="element-2369" data-mp="3bisc2m0z9zev">Demande d’échange envoyée</h3>
                <p className="mt-1 text-sm text-slate-600" data-id="element-2370" data-mp="2xjsntfhdnmj8">
                  {sent.offeredBy.split(' · ')[0]} doit maintenant accepter l’échange vers {sent.day}{' '}
                  {sent.time}. Vous gardez votre créneau actuel jusqu’à la confirmation, et vous serez avisée
                  dans Notifications.
                </p>
                <button type="button" onClick={onClose} className="mt-5 w-full rounded-full bg-ville-navy px-6 py-3 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2" data-id="element-2371" data-mp="3gh3kqtfje3vi">
                  Terminer
                </button>
              </div> : <div className="mt-6" data-id="element-2372" data-mp="21h4ptjgauksc">
                <p className="text-[15px] font-bold text-ville-navy" data-id="element-2373" data-mp="1wl64y5u6jkwo">
                  {offers.length} créneaux offerts par d’autres familles
                </p>
                <p className="mt-1 text-sm text-slate-500" data-id="element-2374" data-mp="2bb55gf97j8fb">
                  Choisissez le créneau souhaité. L’échange prend effet dès que l’autre famille accepte.
                </p>

                <ul className="mt-4 space-y-3" data-id="element-2375" data-mp="1impr7uxwgu51">
                  {offers.map(offer => {
              const active = selected === offer.id;
              return <li key={offer.id} data-id="element-2376" data-mp="1skdtd5xu53x8">
                        <button type="button" aria-pressed={active} onClick={() => setSelected(offer.id)} className={`w-full rounded-2xl border p-4 text-left transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo ${active ? 'border-ville-navy bg-ville-sand' : 'border-slate-200 hover:border-ville-indigo'}`} data-id="element-2377" data-mp="1njzn9tfso41u">
                          <div className="flex flex-wrap items-center justify-between gap-2" data-id="element-2378" data-mp="3g0sj83pjhs6s">
                            <p className="text-sm font-extrabold text-ville-navy" data-id="element-2379" data-mp="3kytzyhzhtff3">
                              {offer.day} · {offer.time}
                            </p>
                            <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold ${fitStyles[offer.fit]}`} data-id="element-2380" data-mp="2s9vhfjrgkb99">
                              {offer.fit === 'conflit' ? <span className="inline-flex items-center gap-1" data-id="element-2381" data-mp="322knu085iyxq">
                                  <TriangleAlertIcon className="h-3 w-3" aria-hidden="true" data-id="element-2382" data-mp="24kkkyeh5530x" />
                                  {offer.fitLabel}
                                </span> : offer.fitLabel}
                            </span>
                          </div>
                          <dl className="mt-2 space-y-1 text-[13px] text-slate-600" data-id="element-2383" data-mp="hnusb4tep11h">
                            <div className="flex items-center gap-1.5" data-id="element-2384" data-mp="cpjjmxeukwku">
                              <MapPinIcon className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden="true" data-id="element-2385" data-mp="crdz3w7cnwhj" />
                              <dt className="sr-only" data-id="element-2386" data-mp="2zhx4a257m81">Lieu</dt>
                              <dd data-id="element-2387" data-mp="2xx3qnrke9br">{offer.place}</dd>
                            </div>
                            <div className="flex items-center gap-1.5" data-id="element-2388" data-mp="3v42a8iuu2o7l">
                              <UsersIcon className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden="true" data-id="element-2389" data-mp="3q4pmzx01vf4h" />
                              <dt className="sr-only" data-id="element-2390" data-mp="3l5cqslnagvp8">Groupe</dt>
                              <dd data-id="element-2391" data-mp="3g603jzsi9mm4">{offer.group}</dd>
                            </div>
                            <div className="flex items-center gap-1.5" data-id="element-2392" data-mp="3g831d5zwgk1e">
                              <ClockIcon className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden="true" data-id="element-2393" data-mp="18vb9fjfhplf6" />
                              <dt className="sr-only" data-id="element-2394" data-mp="1cvlpjowzxs7r">Offert par</dt>
                              <dd data-id="element-2395" data-mp="yvke5fj3v2e1">{offer.offeredBy}</dd>
                            </div>
                          </dl>
                          {offer.note && <p className="mt-2 text-[13px] font-semibold text-ville-deep" data-id="element-2396" data-mp="p6u0v7im7rzq">{offer.note}</p>}
                        </button>
                      </li>;
            })}
                </ul>

                <button type="button" onClick={confirm} disabled={!selected} className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-ville-navy px-6 py-3 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:translate-y-0" data-id="element-2397" data-mp="g7p6mde36ktd">
                  <ArrowLeftRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-2398" data-mp="1j5mkych6hjf" />
                  Proposer l’échange
                </button>
              </div>}
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}